import React, { useRef, useState } from "react";
import { MdOutlineAdd } from "react-icons/md";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  Col,
  Container,
  Row,
} from "reactstrap";
import Image from "next/image";
import moment from "moment";
import { useRouter } from "next/router";

import airmilesContent from "@/data/airmiles-info.json";
import { routes } from "@/utils/routes";
import { airmilesSmallLogo, logo, smallLogo } from "@/utils/images";
// import { aboutUs } from "@/utils/content";
import classes from "@/styles/airmiles/airmiles-main.module.css";
import Hero from "../generic/hero";
import { useIsMobile } from "@/hooks/useIsMobile";

const checkIfBonusOfferActive = (
  activeDays: { start: Date; end: Date }[],
  padDay?: boolean
) => {
  let result = false;
  if (activeDays.length > 0) {
    const { start, end } = activeDays[activeDays.length - 1];
    if (
      moment().isBetween(moment(start), moment(end).add(padDay ? 1 : 0, "d"))
    ) {
      result = true;
      return result;
    }
  }
  return result;
};

const getLastBonusOfferActiveDateRange = (
  activeDays: { start: Date; end: Date }[]
) => {
  let result = "Bonus offer not valid. ";
  if (activeDays.length > 0) {
    const { start, end } = activeDays[activeDays.length - 1];
    const mStart = moment(start);
    const mEnd = moment(end).endOf("day");
    return `Bonus offer valid from ${mStart.format("LL")} till ${mEnd.format(
      "LL"
    )}. `;
  }
  return result;
};

// uses last object in active days
// remember for date objects, month 0 = Jan ... month 11 = Dec
// Also there is a database trigger that needs to be updated with the correct dates
// after updating this variable below
const signUpBonusOfferActiveDays = [
  {
    start: new Date(2022, 10, 14, 0, 0, 1), // Nov. 14
    end: new Date(2022, 11, 16, 23, 59, 59), // Dec. 16
  },
];

// update the variable above/below by adding the most recent date range for the
// contest at the end of the array
// the last range will determine whether it is active or not
const contestOfferActiveDays = [
  {
    start: new Date(2022, 11, 22, 0, 0, 1), // Dec. 22
    end: new Date(2023, 0, 31, 23, 59, 59), // Jan. 31
  },
  {
    start: new Date(2023, 1, 15, 0, 0, 1), // Feb. 15
    end: new Date(2023, 3, 15, 23, 59, 59), // Apr. 15
  },
];

const AirmilesMain = () => {
  const router = useRouter();
  const termsRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState("");

  const handleTermsClick = () => {
    if (termsRef.current) {
      window.scrollTo({ top: termsRef.current.offsetTop, behavior: "smooth" });
    } else {
      router.push(routes.home);
    }
  };

  const handleTermLinkClick = (e: any, termsId: string) => {
    e.preventDefault();
    const terms = document.getElementById(termsId);
    if (terms) {
      window.scrollTo({ top: terms.offsetTop, behavior: "smooth" });
    }
  };

  //@ts-ignore
  const toggle = (id) => {
    if (open === id) {
      //@ts-ignore
      setOpen();
    } else {
      setOpen(id);
    }
  };

  return (
    <main className={classes.container}>
      <section className="my-5">
        <Container
          className={`${
            classes.heroGrig
          } d-flex justify-content-between align-items-center ${
            isMobile ? "" : "w-75"
          }`}
        >
          <Image
            className={`${isMobile ? "me-4" : "ms-5"} img-fluid`}
            src={isMobile ? smallLogo : logo}
            alt="ShipVista logo"
            height={70}
            width={isMobile ? 70 : 300}
          />
          <MdOutlineAdd
            className={`text-darkblue ${classes.svgBlock}`}
            size={50}
          />
          <Image
            className={`img-fluid ${isMobile ? "ms-4" : "me-5"}`}
            src={airmilesSmallLogo}
            alt="Air Miles small logo"
            height={150}
            width={isMobile ? 70 : 170}
          />
        </Container>
      </section>
      <hr />
      <section className="my-5">
        <Container>
          <h3 className="text-center text-grey my-5 fw-bold">
            {airmilesContent.title2}
          </h3>
          <Row className={isMobile ? "" : `w-75 mx-auto`}>
            {airmilesContent.offerCards?.map((oc, i) => (
              <Col xs={12} sm={6} key={i}>
                <Card className={classes.card}>
                  <div className={classes.top}>
                    <a
                      href={oc.link + (i + 1)}
                      onClick={(e) => handleTermLinkClick(e, oc.link + (i + 1))}
                      className={classes.termLink}
                    >
                      {i + 1}
                    </a>
                    <span className={classes.get}>{oc.title}</span>
                    <span className={classes.amount}>{oc.amount}</span>
                    <span className={classes.reward}>{oc.offerType}</span>
                  </div>
                  <div className={classes.bottom}>
                    <p>{oc.desc}</p>
                    {oc.timeDesc && (
                      <div className="text-center">
                        <p className="fw-bold text-blue">{oc.timeDesc}</p>
                        <small className="fw-bold text-black">
                          {checkIfBonusOfferActive(contestOfferActiveDays)
                            ? "Active"
                            : "Not Active"}
                        </small>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
      <section className="my-5">
        <Container>
          <h3 className="text-center text-grey my-4">
            {airmilesContent.title3}
          </h3>
          <div>
            {airmilesContent.contestCards?.map((cc, i) => (
              <Col key={i}>
                <Card className={`${classes.card} ${classes.card2}`}>
                  <div className={classes.top}>
                    <span className={classes.get}>{cc.title}</span>
                    <span className={classes.amount}>{cc.amount}</span>
                    <div className={classes.reward}>
                      <span className="offerType">{cc.offerType}</span>
                      <span className="extra">{cc.offerTypeExtra}</span>
                    </div>
                  </div>
                  <div className={classes.bottom}>
                    <Button
                      variant="outlined"
                      onClick={() => router.push(routes.airmilesGiveAway)}
                      className={`d-block mx-auto my-3 ${classes.btn}`}
                    >
                      {cc.btnTnC}
                    </Button>
                    <div className="text-center">
                      <p className="fw-bold text-blue mb-1">{cc.timeDesc}</p>
                      <small className="fw-bold text-black">
                        {checkIfBonusOfferActive(contestOfferActiveDays)
                          ? "Active"
                          : "Not Active"}
                      </small>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </div>
        </Container>
      </section>
      <section className="my-5">
        <Container>
          <h3 className="text-center text-grey">{airmilesContent.title4}</h3>
          {/*@ts-ignore*/}
          <Accordion open={open} toggle={toggle}>
            <Row>
              {airmilesContent.faqs?.map((f, i) => (
                <Col xs={12} lg={6} key={f.title}>
                  <AccordionItem className="my-3 shadow-sm">
                    <AccordionHeader targetId={(i + 1).toString()}>
                      {f.title}
                    </AccordionHeader>
                    <AccordionBody accordionId={(i + 1).toString()}>
                      <p dangerouslySetInnerHTML={{ __html: f.desc }} />
                      {f.desc2 && (
                        <p dangerouslySetInnerHTML={{ __html: f.desc }} />
                      )}
                    </AccordionBody>
                  </AccordionItem>
                </Col>
              ))}
            </Row>
          </Accordion>
        </Container>
      </section>
      <section className="my-5">
        <Container>
          <h3 className="text-center text-grey">
            {airmilesContent.termsTitle}
          </h3>
          <h6 className="text-center text-grey">
            {airmilesContent.termsSubTitle}
          </h6>
          {airmilesContent.terms?.map((t, i) => (
            <p key={i}>
              {`${i + 1}) `}
              {t.includes("{0}")
                ? t.replace(
                    "{0}",
                    getLastBonusOfferActiveDateRange(signUpBonusOfferActiveDays)
                  )
                : t}
            </p>
          ))}
          <p>{airmilesContent.termsPara1}</p>
          <p>{airmilesContent.termsPara2}</p>
        </Container>
      </section>
    </main>
  );
};

export default AirmilesMain;
